import { createFileRoute, Link } from '@tanstack/react-router'
import { Logo } from '@/components/Logo'

export const Route = createFileRoute('/delete-account')({
  component: DeleteAccount,
})

function DeleteAccount() {
  return (
    <main className="bg-muted min-h-screen py-12">
      <header className="mx-auto mb-12 max-w-3xl px-8">
        <Link to="/" aria-label="Go to homepage">
          <Logo size="md" />
        </Link>
      </header>

      <article className="mx-auto max-w-3xl px-8">
        <h1 className="text-foreground mb-4 text-4xl font-bold">Delete your Markur account</h1>
        <p className="text-muted-foreground mb-12 text-sm">Last updated: 19 August 2026</p>

        <div className="text-foreground space-y-10">
          <section className="space-y-4">
            <p>
              This page explains how to permanently delete your account and the data associated with
              it from <strong>Markur</strong> (Android app on Google Play, listed under developer
              name <strong>Markur Health</strong>, operated by{' '}
              <strong>Markur Health Limited</strong>, an Irish private limited company — Companies
              Registration Office number 817464).
            </p>
            <p>
              You can request deletion in two ways: directly from inside the app (recommended,
              instant), or by emailing our support team (use this if you no longer have the app
              installed).
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">1. From inside the app (instant)</h2>
            <p>If you still have Markur installed on your device:</p>
            <ol className="list-decimal space-y-2 pl-6">
              <li>Open Markur and sign in.</li>
              <li>
                Tap the profile/settings icon, then go to{' '}
                <strong>Settings → Account → Delete Account</strong>.
              </li>
              <li>Confirm the deletion when prompted.</li>
            </ol>
            <p>
              The deletion is processed immediately. Your data is removed from our active systems
              within seconds. No support contact is required.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">2. By email (if you've uninstalled the app)</h2>
            <p>Email our support team:</p>
            <p>
              <a
                href="mailto:support@markurhealth.com?subject=Account%20deletion%20request&body=I%20would%20like%20to%20request%20deletion%20of%20my%20Markur%20account.%0A%0AThe%20email%20address%20associated%20with%20my%20account%20is%3A%20%5Byour%20email%5D"
                className="font-medium underline"
              >
                support@markurhealth.com
              </a>
            </p>
            <p>
              Use the subject line <em>"Account deletion request"</em> and send from the email
              address tied to your Markur account. We need to receive the request from the account
              email so we can verify the request without exposing your data to a third party.
            </p>
            <p>
              We respond to deletion requests within 30 days as required by GDPR Article 12,
              typically within 1–3 business days.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">3. What gets deleted</h2>
            <p>When your account is deleted, the following data is permanently removed:</p>
            <ul className="list-disc space-y-2 pl-6">
              <li>Your user profile (name, email, date of birth, sex, consent timestamps)</li>
              <li>All blood biomarker values you have entered or imported</li>
              <li>Lab report uploads (images and PDF files)</li>
              <li>AI chat transcripts and message feedback</li>
              <li>App preferences and settings</li>
              <li>
                Family connections you initiated and shared biomarker history where Markur is the
                data controller
              </li>
              <li>Authentication session tokens</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">4. What is retained, and for how long</h2>
            <p>
              After deletion, Markur retains a minimal set of records strictly to comply with legal
              obligations and to defend against fraud:
            </p>
            <ul className="list-disc space-y-2 pl-6">
              <li>
                <strong>Audit log entries</strong> recording the deletion event itself (no personal
                data — just a timestamp and a hashed account identifier). Retained indefinitely as
                proof that the deletion was performed.
              </li>
              <li>
                <strong>Anonymised analytics events</strong> (Mixpanel) tied to the deleted
                account's anonymous identifier. These cannot be re-linked to you.
              </li>
              <li>
                <strong>Backups</strong> of our database may contain your data for up to 30 days
                after deletion (the rolling backup window), after which they are permanently
                overwritten. Backups are not used for ordinary operations and are not accessible by
                the application.
              </li>
            </ul>
            <p>
              No biomarker values, lab reports, AI chat content, or personal profile fields are
              retained after deletion.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">5. Steps before requesting deletion</h2>
            <p>
              No prerequisite steps are required. Markur is free to use, has no paid subscriptions,
              and no in-app purchases. You do not need to cancel anything before requesting
              deletion.
            </p>
            <p>
              If you want a copy of your data before deletion, you can export it from{' '}
              <strong>Settings → Data &amp; Privacy → Export my data</strong> inside the app. The
              export is a JSON file containing all the data we hold for you.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">6. Operator</h2>
            <p>
              Markur is operated by <strong>Markur Health Limited</strong>, an Irish private limited
              company.
            </p>
            <p className="text-muted-foreground">Companies Registration Office number: 817464</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-2xl font-semibold">7. Questions</h2>
            <p>
              For questions about deletion, data we hold about you, or any other privacy concern,
              see our{' '}
              <Link to="/privacy-policy" className="font-medium underline">
                Privacy Policy
              </Link>{' '}
              or email{' '}
              <a href="mailto:support@markurhealth.com" className="font-medium underline">
                support@markurhealth.com
              </a>
              .
            </p>
          </section>
        </div>
      </article>
    </main>
  )
}
